/**
 * Content Hash Utility
 * Generates SHA-256 hashes for course content integrity verification
 * Requirements: 7.3, 7.5
 */

import type { ExportedCourse } from '@course-authoring/shared-types';

/**
 * Generates a deterministic SHA-256 hash for course content.
 * The hash is used for integrity verification in the Course_Manifest.
 * 
 * @param course - The exported course content to hash
 * @returns Promise resolving to the hex-encoded SHA-256 hash string
 * 
 * Requirements: 7.3, 7.5
 */
export async function generateContentHash(course: ExportedCourse): Promise<string> {
  // Serialize course to JSON with sorted keys for deterministic output
  const content = JSON.stringify(course, sortedReplacer);
  
  // Encode the string as UTF-8 bytes
  const encoder = new TextEncoder();
  const data = encoder.encode(content);
  
  // Generate SHA-256 hash using Web Crypto API
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  
  // Convert hash buffer to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * JSON replacer function that sorts object keys for deterministic serialization.
 * This ensures the same content always produces the same hash regardless of
 * property insertion order.
 */
function sortedReplacer(_key: string, value: unknown): unknown {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce((sorted: Record<string, unknown>, key: string) => {
        sorted[key] = (value as Record<string, unknown>)[key];
        return sorted;
      }, {});
  }
  return value;
}

/**
 * Generates a content hash from a raw string.
 * Useful for hashing arbitrary content like manifest files.
 * 
 * @param content - The string content to hash
 * @returns Promise resolving to the hex-encoded SHA-256 hash string
 */
export async function generateStringHash(content: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(content);
  
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}
